import { FormSelect, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { updateUserAccountSettings } from 'modules/profile/account-settings/api/account-settings-api'
import AccountSettingsSkeleton from 'modules/profile/account-settings/components/account-settings-skeleton'
import { AccountSettingsLoco } from 'modules/profile/account-settings/constants/account-settings-loco'
import { useAccountSettings } from 'modules/profile/account-settings/hooks/use-account-settings'
import { useAccountSettingsTimezones } from 'modules/profile/account-settings/hooks/user-account-settings-timezones'
import {
  AccountSettingsEditableInterface,
  AccountSettingsErrorResponseContentInterface,
  AccountSettingsErrorsInterface,
  AccountSettingsInterface,
} from 'modules/profile/account-settings/types/account-settings-interface'
import {
  getFormatDataOptions,
  getFormatTimeOptions,
  getTimezonesDataOptions,
} from 'modules/profile/account-settings/utils/formats'
import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const defaultErrors: AccountSettingsErrorsInterface = {
  dateFormat: '',
  timeFormat: '',
  timezone: '',
}

const defaultValues: AccountSettingsEditableInterface = {
  dateFormat: undefined,
  timeFormat: undefined,
  timezone: '',
}

const AccountSettings: NextPage = () => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [tempState, setTempState] = useState<AccountSettingsEditableInterface>(defaultValues)
  const [errors, setErrors] = useState<AccountSettingsErrorsInterface>(defaultErrors)
  const [isFetching, setIsFetching] = useState(false)
  const [timezoneDataOptions, setTimezoneDataOptions] = useState<{ id: string; caption: string }[]>(
    [],
  )
  const { userAccount, mutate } = useAccountSettings()
  const { data } = useAccountSettingsTimezones()
  const dateFormatDataOptions = getFormatDataOptions(user?.timezone)
  const timeFormatDataOptions = getFormatTimeOptions(user?.timezone)

  const onChangeAccountSettings = async () => {
    try {
      const data: AccountSettingsInterface = {
        dateFormat: tempState.dateFormat,
        timeFormat: tempState.timeFormat,
        user: {
          timezone: tempState.timezone,
        },
      }
      setIsFetching(true)
      const response = await updateUserAccountSettings(data)
      await mutate(response.data, false)
      setIsFetching(false)
      toast.success(t('global.changes_saved'))
      setErrors(defaultErrors)
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const accountErrors = e.errors as unknown as AccountSettingsErrorResponseContentInterface
        setErrors({
          dateFormat: accountErrors.fields.dateFormat?.join(''),
          timeFormat: accountErrors.fields.timeFormat?.join(''),
          timezone: accountErrors.fields.timezone?.join(''),
        })
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  useEffect(() => {
    if (userAccount) {
      setTempState({
        dateFormat: userAccount.dateFormat,
        timeFormat: userAccount.timeFormat,
        timezone: userAccount.user.timezone,
      })
    }
  }, [userAccount])

  useEffect(() => {
    if (data) {
      setTimezoneDataOptions(getTimezonesDataOptions(data.timezones))
    }
  }, [data])

  return (
    <SettingsLayout
      titles={[{ caption: t(settingsMenuLocoKeys.account), isStatic: true }]}
      isFetching={isFetching}
      onSave={onChangeAccountSettings}
      disabled={!userAccount}
    >
      {userAccount ? (
        <div className="bg-background-secondary flex h-fit w-full flex-col gap-5 overflow-x-hidden rounded-md">
          <div className="flex flex-col gap-7 p-5 lg:gap-10">
            <div className="flex flex-col justify-between gap-7 lg:flex-row lg:gap-10 [&>*]:flex-1">
              <FormSelect
                placeholder={t('dashboard.user_settings.account.timezone_title')}
                data={timezoneDataOptions}
                label={t('dashboard.user_settings.account.timezone_title')}
                className={'sm:min-w-0'}
                required
                value={tempState.timezone || undefined}
                onChange={value => value && setTempState(prev => ({ ...prev, timezone: value }))}
                error={errors.timezone}
                withoutCloseIcon
              />
              <FormSelect
                placeholder={t('dashboard.user_settings.account.date_format_title')}
                className={'sm:min-w-0'}
                data={dateFormatDataOptions}
                label={t('dashboard.user_settings.account.date_format_title')}
                required
                value={tempState.dateFormat}
                onChange={value => setTempState(prev => ({ ...prev, dateFormat: value }))}
                error={errors.dateFormat}
                withoutCloseIcon
              />
              <FormSelect
                placeholder={t('dashboard.user_settings.account.time_format_title')}
                className={'sm:min-w-0'}
                data={timeFormatDataOptions}
                label={t('dashboard.user_settings.account.time_format_title')}
                required
                value={tempState.timeFormat || undefined}
                onChange={value => setTempState(prev => ({ ...prev, timeFormat: value }))}
                error={errors.timeFormat}
                withoutCloseIcon
              />
            </div>
          </div>
        </div>
      ) : (
        <AccountSettingsSkeleton />
      )}
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(AccountSettingsLoco, 'global.settings')

export default AccountSettings
